import { logger } from '../../../utils/logger.js'
import { LitElement, html, css, TemplateResult } from 'lit'
import { property, state } from 'lit/decorators.js'
import maplibregl from 'maplibre-gl'
import type { MapFeature } from '../../../schemas/map-feature.js'
import { tripFeatureManager } from '../../../services/trip-feature-manager.js'
import { applyFillPatternToLayer } from './fill-patterns.js'
import { computeLabelStyle } from '../../../lib/shape-utils.js'

const log = logger.create('map')

/** Marker with optional index metadata used by vertex/midpoint editing */
export interface IndexedMarker extends maplibregl.Marker {
  _vertexIndex?: number
  _midpointIndex?: number
}

export abstract class BaseShape extends LitElement {
  @property({ type: Object }) feature!: MapFeature
  @property({ type: Object }) map!: maplibregl.Map
  @state() isEditing = false
  @state() isSelected = false

  protected sourceId!: string
  protected layerIds: string[] = []
  protected handles: maplibregl.Marker[] = []
  protected labelMarker?: maplibregl.Marker | undefined
  protected isDragging = false
  private _hoverCount = 0
  static ignoreNextMapClick = 0

  static override styles = css`
    :host {
      display: none;
    }

    .edit-handle {
      background: white;
      border: 2px solid #666;
      width: 8px;
      height: 8px;
      z-index: 2000 !important;
      cursor: grab !important;
    }

    .edit-handle:hover {
      transform: scale(1.2) !important;
    }

    .edit-handle:active,
    .edit-handle.dragging {
      cursor: grabbing !important;
      transform: scale(1.1) !important;
    }

    .drag-handle {
      width: 10px !important;
      height: 10px !important;
    }

    .vertex-handle {
      background: #ffffff !important;
      border: 1px solid #000000 !important;
    }

    .radius-handle {
      background: #ffffff !important;
      border: 2px solid #3f5a7a !important;
    }

    .corner-handle {
      background: #ffffff !important;
      border: 2px solid #3f5a7a !important;
    }

    .midpoint-handle {
      background: #fbbf24 !important;
      border: 2px solid #f59e0b !important;
    }
  `

  override connectedCallback(): void {
    super.connectedCallback()
    this.sourceId = `feature-${this.feature.id}`
    this.createLayers()
    this.addToMap()
    this.setupEventListeners()
    requestAnimationFrame(() => {
      this.applyFillPatternToLayer()
      this.refreshLabel()
    })
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeFromMap()
    this.removeEventListeners()
  }

  protected abstract createLayers(): void
  protected abstract createHandles(): void
  abstract updateSource(): void
  protected abstract getGeoJSON(): GeoJSON.Feature
  protected abstract updateShapePreview(handleType: string, newPosition: maplibregl.LngLat): void
  protected abstract applyGeometryChange(
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void>
  protected abstract updateAllHandlePositions(centerPosition: maplibregl.LngLat): void

  refreshStyle(): void {
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        const styleOptions = this.getStyleOptions()
        Object.entries(styleOptions).forEach(([property, value]) => {
          this.map.setPaintProperty(layerId, property, value)
        })
      }
    })
    this.applyFillPatternToLayer()
  }

  refreshLabel(): void {
    this.removeLabelMarker()
    const label = this.feature.label
    if (!label || !label.visible || !label.text) {
      return
    }
    const position = this.getLabelPosition()
    if (!position) {
      return
    }
    const markerSize = this.feature.style?.markerSize ?? 12
    const strokeWidth = this.feature.style?.strokeWidth ?? 2
    const containerW = 300
    const style = computeLabelStyle(label, markerSize, strokeWidth, containerW)
    const labelHtml = `<div style="color:${style.color};font-size:${style.fontSize}px;font-family:${style.fontFamily};font-weight:bold;white-space:nowrap;text-shadow:${style.textShadow};pointer-events:none;text-align:${style.textAlign};padding:${style.padding};">${label.text}</div>`
    const element = document.createElement('div')
    element.innerHTML = labelHtml
    element.style.width = `${containerW}px`
    this.labelMarker = new maplibregl.Marker({
      element,
      anchor: 'center',
      offset: [0, style.anchorOffsetY],
    })
      .setLngLat(position)
      .addTo(this.map)
  }

  protected getLabelPosition(): [number, number] | null {
    const geoJSON = this.getGeoJSON()
    if (geoJSON.geometry.type === 'Point') {
      return geoJSON.geometry.coordinates as [number, number]
    }
    const bounds = this.getLayerBounds()
    if (bounds) {
      const center = bounds.getCenter()
      return [center.lng, center.lat]
    }
    return null
  }

  private removeLabelMarker(): void {
    if (this.labelMarker) {
      this.labelMarker.remove()
      this.labelMarker = undefined
    }
  }

  applyFillPatternToLayer(): void {
    if (!this.feature.style) {
      return
    }
    const fillLayerId = `${this.sourceId}-fill`
    const pattern = this.feature.style.fillPattern
    const color = this.feature.style.fillColor ?? '#3f5a7a'
    const opacity = this.feature.style.fillOpacity ?? 0.3
    applyFillPatternToLayer(this.map, fillLayerId, pattern, color, opacity)
  }

  protected getStyleOptions(): Record<string, unknown> {
    return {}
  }

  protected addToMap(): void {}

  setVisible(visible: boolean): void {
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
      }
    })
    if (this.labelMarker) {
      if (visible) {
        this.labelMarker.addTo(this.map)
      } else {
        this.labelMarker.remove()
      }
    }
  }

  removeFromMap(): void {
    log.debug('🗑️ BaseShape removeFromMap called for:', this.feature.id)

    if (!(this.map as unknown as Record<string, unknown>).style) {
      this.removeLabelMarker()
      this.removeHandles()
      this.layerIds = []
      return
    }

    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId)
        log.debug('✅ Layer removed:', layerId)
      }
    })

    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId)
      log.debug('✅ Source removed:', this.sourceId)
    }

    this.removeLabelMarker()
    this.removeHandles()

    this.layerIds = []

    log.debug('✅ BaseShape removeFromMap complete')
  }

  enterEditMode(): void {
    if (this.isEditing) {
      return
    }

    this.isEditing = true
    this.createHandles()

    this.dispatchEvent(
      new CustomEvent('shape-selected', {
        detail: { shapeId: this.feature.id },
        bubbles: true,
      })
    )
  }

  exitEditMode(): void {
    if (!this.isEditing) {
      return
    }

    this.isEditing = false
    this.removeHandles()
    // Leaving edit deselects this shape (only the one that was editing — shapes
    // that weren't editing returned above, so box-selections are untouched).
    this.setSelected(false)
  }

  setSelected(selected: boolean): void {
    this.isSelected = selected
    this.applySelectionStyle(selected)
  }

  protected applySelectionStyle(selected: boolean): void {
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        const currentStroke =
          this.map.getPaintProperty(layerId, 'fill-stroke-color') ??
          this.map.getPaintProperty(layerId, 'line-color') ??
          this.map.getPaintProperty(layerId, 'circle-stroke-color')
        if (currentStroke !== undefined) {
          const glowColor = selected ? 'rgba(37, 99, 235, 1)' : currentStroke
          if (this.map.getPaintProperty(layerId, 'fill-stroke-color') !== undefined) {
            this.map.setPaintProperty(layerId, 'fill-stroke-color', glowColor)
            this.map.setPaintProperty(layerId, 'fill-stroke-width', selected ? 4 : 2)
          }
          if (this.map.getPaintProperty(layerId, 'line-color') !== undefined) {
            this.map.setPaintProperty(layerId, 'line-color', glowColor)
            this.map.setPaintProperty(layerId, 'line-width', selected ? 4 : 2)
          }
          if (this.map.getPaintProperty(layerId, 'circle-stroke-color') !== undefined) {
            this.map.setPaintProperty(layerId, 'circle-stroke-color', glowColor)
            this.map.setPaintProperty(layerId, 'circle-stroke-width', selected ? 4 : 2)
          }
        }
      }
    })
  }

  async deleteFeature(): Promise<void> {
    log.debug('🗑️ BaseShape.deleteFeature() called for:', this.feature.id)
    try {
      log.debug('🗑️ Deleting from database...')
      await tripFeatureManager.deleteFeature(this.feature.id)
      log.debug('🗑️ Database deletion complete')

      log.debug('🗑️ Dispatching shape-deleted event...')
      this.dispatchEvent(
        new CustomEvent('shape-deleted', {
          detail: { featureId: this.feature.id },
          bubbles: true,
        })
      )
      log.debug('🗑️ Shape-deleted event dispatched')

      log.debug('🗑️ Removing from map...')
      this.removeFromMap()
      log.debug('🗑️ Map removal complete')

      log.debug('🗑️ Removing from DOM...')
      this.remove()
      log.debug('🗑️ DOM removal complete')

      log.debug('🗑️ Shape deleted successfully:', this.feature.id)
    } catch (error) {
      log.error('❌ Failed to delete shape:', error)
    }
  }

  protected removeHandles(): void {
    this.handles.forEach(handle => handle.remove())
    this.handles = []
  }

  protected setupEventListeners(): void {
    document.addEventListener(
      'shape-selected',
      this.handleOtherShapeSelected.bind(this) as EventListener
    )

    // Per-layer click/touch selection is intentionally NOT registered here.
    // GestureController is the sole tap recogniser; it hit-tests user shapes via
    // their `feature-<id>` sources and routes the tap (identify / toggle-select).
    // Only hover affordances live on the shape's layers.
    this.layerIds.forEach(layerId => {
      this.map.on('mouseenter', layerId, () => {
        this._hoverCount = this._hoverCount + 1
        this.map
          .getCanvas()
          .style.setProperty('cursor', this.isEditing ? 'move' : 'pointer', 'important')
      })
      this.map.on('mouseleave', layerId, () => {
        this._hoverCount = Math.max(0, this._hoverCount - 1)
        if (this._hoverCount === 0) {
          this.map.getCanvas().style.removeProperty('cursor')
        }
      })
    })
  }

  protected removeEventListeners(): void {
    document.removeEventListener(
      'shape-selected',
      this.handleOtherShapeSelected.bind(this) as EventListener
    )

    // MapLibre supports off(type, layer) to remove all listeners — not in TS types
    const mapOff = this.map.off.bind(this.map) as unknown as (type: string, layer: string) => void
    this.layerIds.forEach(layerId => {
      mapOff('click', layerId)
      mapOff('mouseenter', layerId)
      mapOff('mouseleave', layerId)
    })
  }

  protected handleOtherShapeSelected(event: Event): void {
    const customEvent = event as CustomEvent
    if (customEvent.detail.shapeId !== this.feature.id) {
      this.exitEditMode()
    }
  }

  protected createHandle(
    position: [number, number],
    handleType: string,
    _handleClass: string = 'edit-handle'
  ): maplibregl.Marker {
    const element = document.createElement('div')
    element.style.cursor = 'grab'
    element.style.zIndex = '2000'

    switch (handleType) {
      case 'vertex':
        element.style.width = '10px'
        element.style.height = '10px'
        element.style.background = '#ffffff'
        element.style.border = '1px solid #000000'
        element.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'midpoint':
        element.style.width = '6px'
        element.style.height = '6px'
        element.style.background = '#ffffff'
        element.style.border = '1px solid #000000'
        element.style.opacity = '0.8'
        element.style.boxShadow = '0 1px 3px rgba(0,0,0,0.5)'
        break
      case 'drag':
        element.style.width = '12px'
        element.style.height = '12px'
        element.style.background = '#3f5a7a'
        element.style.border = '2px solid #3f5a7a'
        element.style.borderRadius = '50%'
        element.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'corner':
        element.style.width = '10px'
        element.style.height = '10px'
        element.style.background = '#ffffff'
        element.style.border = '2px solid #3f5a7a'
        element.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      case 'radius':
        element.style.width = '10px'
        element.style.height = '10px'
        element.style.background = '#ffffff'
        element.style.border = '2px solid #3f5a7a'
        element.style.borderRadius = '50%'
        element.style.boxShadow = '0 1px 4px rgba(0,0,0,0.5)'
        break
      default:
        element.style.width = '8px'
        element.style.height = '8px'
        element.style.background = '#ffffff'
        element.style.border = '2px solid #666'
        break
    }

    const marker = new maplibregl.Marker({ element, draggable: false })
      .setLngLat(position)
      .addTo(this.map)

    this.setupHandleDragging(marker, handleType)
    return marker
  }

  protected setupHandleDragging(marker: maplibregl.Marker, handleType: string): void {
    const element = marker.getElement()

    const mouseDownHandler = (e: MouseEvent): void => {
      e.stopPropagation()
      e.preventDefault()
      this.startHandleDrag(marker, handleType)
    }

    const touchStartHandler = (e: TouchEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      this.startHandleDrag(marker, handleType)
    }

    element.addEventListener('mousedown', mouseDownHandler)
    element.addEventListener('touchstart', touchStartHandler, { passive: false })
  }

  protected touchToLngLat(touch: Touch): maplibregl.LngLat {
    const container = this.map.getContainer()
    const rect = container.getBoundingClientRect()
    return this.map.unproject([touch.clientX - rect.left, touch.clientY - rect.top])
  }

  private _mouseMoveHandler?: ((e: MouseEvent) => void) | undefined
  private _mouseUpHandler?: ((e: MouseEvent) => void) | undefined
  private _touchMoveHandler?: ((e: TouchEvent) => void) | undefined
  private _touchEndHandler?: ((e: TouchEvent) => void) | undefined

  protected startHandleDrag(marker: maplibregl.Marker, handleType: string): void {
    if (this.isDragging) {
      return
    }

    this.isDragging = true
    this.handles.forEach(h => (h.getElement().style.display = 'none'))

    this._mouseMoveHandler = (e: MouseEvent) => {
      const lngLat = this.map.unproject([
        e.clientX - this.map.getContainer().getBoundingClientRect().left,
        e.clientY - this.map.getContainer().getBoundingClientRect().top,
      ])
      this.handleDrag(marker, handleType, lngLat)
    }

    this._mouseUpHandler = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const lngLat = this.map.unproject([
        e.clientX - this.map.getContainer().getBoundingClientRect().left,
        e.clientY - this.map.getContainer().getBoundingClientRect().top,
      ])
      void this.endHandleDrag(marker, handleType, lngLat)
    }

    this._touchMoveHandler = (e: TouchEvent) => {
      if (!this.isDragging || e.touches.length !== 1) {
        return
      }
      const lngLat = this.touchToLngLat(e.touches[0])
      e.preventDefault()
      this.handleDrag(marker, handleType, lngLat)
    }

    this._touchEndHandler = (e: TouchEvent) => {
      if (!this.isDragging) {
        return
      }
      const lngLat = e.changedTouches.length > 0 ? this.touchToLngLat(e.changedTouches[0]) : null
      if (!lngLat) {
        return
      }
      e.preventDefault()
      void this.endHandleDrag(marker, handleType, lngLat)
    }

    document.addEventListener('mousemove', this._mouseMoveHandler)
    document.addEventListener('mouseup', this._mouseUpHandler)
    this.map
      .getContainer()
      .addEventListener('touchmove', this._touchMoveHandler, { passive: false })
    this.map.getContainer().addEventListener('touchend', this._touchEndHandler, { passive: false })
  }

  protected handleDrag(
    _marker: maplibregl.Marker,
    handleType: string,
    newPosition: maplibregl.LngLat
  ): void {
    if (!this.isDragging) {
      return
    }

    this.updateShapePreview(handleType, newPosition)
  }

  protected async endHandleDrag(
    _marker: maplibregl.Marker,
    handleType: string,
    newPosition: maplibregl.LngLat
  ): Promise<void> {
    if (!this.isDragging) {
      return
    }

    log.debug('🔧 endHandleDrag - start, isEditing:', this.isEditing)

    this.isDragging = false

    if (this._mouseMoveHandler) {
      document.removeEventListener('mousemove', this._mouseMoveHandler)
    }
    if (this._mouseUpHandler) {
      document.removeEventListener('mouseup', this._mouseUpHandler)
    }
    if (this._touchMoveHandler) {
      this.map.getContainer().removeEventListener('touchmove', this._touchMoveHandler)
    }
    if (this._touchEndHandler) {
      this.map.getContainer().removeEventListener('touchend', this._touchEndHandler)
    }

    this._mouseMoveHandler = undefined
    this._mouseUpHandler = undefined
    this._touchMoveHandler = undefined
    this._touchEndHandler = undefined

    BaseShape.ignoreNextMapClick++

    await this.applyGeometryChange(handleType, newPosition)

    log.debug('🔧 endHandleDrag - after applyGeometryChange, isEditing:', this.isEditing)

    if (this.isEditing) {
      this.removeHandles()
      this.createHandles()
    }

    // Reset after handles are rebuilt — any click from this interaction
    // has already been consumed or never fired
    setTimeout(() => {
      BaseShape.ignoreNextMapClick = 0
    }, 0)
  }

  getLayerBounds(): maplibregl.LngLatBounds | null {
    const geoJSON = this.getGeoJSON()

    let coords: number[][] = []

    if (geoJSON.geometry.type === 'Point') {
      coords = [geoJSON.geometry.coordinates as number[]]
    } else if (geoJSON.geometry.type === 'LineString') {
      coords = geoJSON.geometry.coordinates as number[][]
    } else if (geoJSON.geometry.type === 'Polygon') {
      coords = (geoJSON.geometry.coordinates as number[][][])[0]
    }

    if (coords.length === 0) {
      return null
    }

    const bounds = new maplibregl.LngLatBounds()
    coords.forEach(coord => {
      bounds.extend(coord as [number, number])
    })

    return bounds
  }

  override render(): TemplateResult {
    return html``
  }
}
